/**
 * FolderPicker — generic компонент выбора/создания папок-коллекций.
 *
 * Унифицирует UI и логику для двух разделов:
 *   - Аудио: «альбомы» (SunoAlbum + suno-треки)
 *   - Презентации: «папки» (PresentationFolder + презентации)
 *
 * Разделы отличаются только API-эндпоинтами и лейблами — всё остальное
 * (multiselect чекбоксы, indeterminate state, create-inline форма, covers,
 * optimistic updates, compact-режим для popover'а) одинаковое. Раньше было
 * два дубля (AddToAlbumList+AddToAlbumSheet vs AddToFolderSheet), теперь
 * один компонент + два тонких адаптера.
 *
 * Экспортирует:
 *   - FolderPickerList — inner body (для accordion в SunoActionsSheet)
 *   - FolderPickerSheet — обёртка в Sheet/AnchoredPopover (для standalone)
 *   - FolderPickerAPI / FolderPickerLabels / FolderLike — types для адаптеров
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import Sheet from './Sheet'
import AnchoredPopover, { useIsDesktopPopover } from './AnchoredPopover'
import { Icon } from '../../public/icons/Icon.tsx'

// ── Типы ──────────────────────────────────────────────────────────

export interface FolderLike {
	id: string
	name: string
	/** URL обложки (аудио). У презентаций null → показываем generic icon. */
	cover_url?: string | null
}

export interface FolderPickerAPI<F extends FolderLike = FolderLike> {
	list: () => Promise<{ folders: F[] }>
	create: (name: string) => Promise<{ ok: boolean; folder?: F; error?: string }>
	addItems: (folderId: string, itemIds: string[]) => Promise<{ ok: boolean }>
	removeItem: (folderId: string, itemId: string) => Promise<{ ok: boolean }>
	/** В каких папках item уже состоит (для initial state чекбоксов). */
	getFolderIdsForItem: (itemId: string) => string[]
	/** Количество items в папке (для бейджа справа от имени). Name варьируется
	 *  в моделях (track_count / item_count) — адаптер разруливает. */
	getFolderItemCount: (folder: F) => number
}

export interface FolderPickerLabels {
	/** Для create-only sheet title («Создать альбом» / «Новая папка»). */
	createTitle: string
	/** Для add-mode с 1 item («Добавить в альбом» / «Выбрать папки»). */
	addSingleTitle: string
	/** Для add-mode с N items («Добавить 5 треков» / «Добавить 5 презентаций»). */
	addMultiTitle: (n: number) => string
	/** Placeholder инпута. */
	inputPlaceholder: string
	/** Кнопка «Создать новый X». */
	createButtonLabel: string
	/** Пустой state после успешной загрузки. */
	emptyText: string
}

// ── List (inner body) ─────────────────────────────────────────────

type CheckboxState = 'unchecked' | 'indeterminate' | 'checked'

interface ListProps<F extends FolderLike> {
	/** Active только когда список реально нужен. Управляет initial fetch. */
	active: boolean
	/** UUID'ы items для add/remove. Пустой → create-only режим. */
	itemIds: string[]
	api: FolderPickerAPI<F>
	labels: FolderPickerLabels
	onChanged: () => void
	/** После успешного создания в create-only — host может закрыться. */
	onAfterCreate?: () => void
	/** Compact (меньше paddings) — когда рендеримся в popover'е. */
	compact?: boolean
}

export function FolderPickerList<F extends FolderLike>({
	active,
	itemIds,
	api,
	labels,
	onChanged,
	onAfterCreate,
	compact,
}: ListProps<F>) {
	const [folders, setFolders] = useState<F[]>([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)
	/** folder_id → оптимистично обновлённое кол-во items. */
	const [localCounts, setLocalCounts] = useState<Map<string, number>>(new Map())
	const [busyFolders, setBusyFolders] = useState<Set<string>>(new Set())

	const [createOpen, setCreateOpen] = useState(false)
	const [newName, setNewName] = useState('')
	const [creating, setCreating] = useState(false)

	const isCreateOnly = itemIds.length === 0

	const loadFolders = useCallback(async () => {
		setLoading(true)
		setError(null)
		try {
			const { folders } = await api.list()
			setFolders(folders)
		} catch (e) {
			setError(e instanceof Error ? e.message : 'Не удалось загрузить')
		} finally {
			setLoading(false)
		}
	}, [api])

	useEffect(() => {
		if (!active) return
		if (!isCreateOnly) loadFolders()
		setLocalCounts(new Map())
		setBusyFolders(new Set())
		setCreateOpen(isCreateOnly)
		setNewName('')
	}, [active, isCreateOnly, loadFolders])

	// Базовые счётчики: сколько itemIds уже в каждой папке — из api.
	const baseCounts = useMemo(() => {
		const map = new Map<string, number>()
		for (const iid of itemIds) {
			for (const fid of api.getFolderIdsForItem(iid)) {
				map.set(fid, (map.get(fid) ?? 0) + 1)
			}
		}
		return map
	}, [itemIds, api])

	const getCount = useCallback(
		(folderId: string): number => localCounts.get(folderId) ?? baseCounts.get(folderId) ?? 0,
		[localCounts, baseCounts],
	)

	const getState = useCallback(
		(folderId: string): CheckboxState => {
			const count = getCount(folderId)
			if (count === 0) return 'unchecked'
			if (count === itemIds.length) return 'checked'
			return 'indeterminate'
		},
		[getCount, itemIds.length],
	)

	const setBusy = useCallback((folderId: string, busy: boolean) => {
		setBusyFolders((prev) => {
			const next = new Set(prev)
			if (busy) next.add(folderId)
			else next.delete(folderId)
			return next
		})
	}, [])

	const handleToggle = useCallback(
		async (folderId: string) => {
			if (busyFolders.has(folderId)) return
			const state = getState(folderId)
			setBusy(folderId, true)
			try {
				if (state === 'checked') {
					await Promise.all(itemIds.map((iid) => api.removeItem(folderId, iid)))
					setLocalCounts((prev) => new Map(prev).set(folderId, 0))
				} else {
					const already = new Set<string>()
					for (const iid of itemIds) {
						if (api.getFolderIdsForItem(iid).includes(folderId)) already.add(iid)
					}
					const toAdd = itemIds.filter((iid) => !already.has(iid))
					if (toAdd.length > 0) await api.addItems(folderId, toAdd)
					setLocalCounts((prev) => new Map(prev).set(folderId, itemIds.length))
				}
				onChanged()
			} catch (e) {
				setError(e instanceof Error ? e.message : 'Ошибка')
			} finally {
				setBusy(folderId, false)
			}
		},
		[busyFolders, getState, itemIds, api, onChanged, setBusy],
	)

	const handleCreateSubmit = useCallback(async () => {
		const name = newName.trim()
		if (!name || creating) return
		setCreating(true)
		setError(null)
		try {
			const res = await api.create(name)
			if (!res.ok || !res.folder) {
				setError(res.error || 'Не удалось создать')
				return
			}
			if (itemIds.length > 0) {
				await api.addItems(res.folder.id, itemIds)
			}
			onChanged()
			if (isCreateOnly) {
				onAfterCreate?.()
				return
			}
			await loadFolders()
			setLocalCounts((prev) => new Map(prev).set(res.folder!.id, itemIds.length))
			setCreateOpen(false)
			setNewName('')
		} catch (e) {
			setError(e instanceof Error ? e.message : 'Не удалось создать')
		} finally {
			setCreating(false)
		}
	}, [newName, creating, itemIds, api, isCreateOnly, loadFolders, onChanged, onAfterCreate])

	return (
		<div className={'add-to-album-list' + (compact ? ' add-to-album-list--compact' : '')}>
			{/* Создать новый */}
			{!createOpen && !isCreateOnly ? (
				<button
					type="button"
					className="add-to-album-list__create-btn"
					onClick={() => setCreateOpen(true)}
				>
					<span className="add-to-album-list__create-icon" aria-hidden="true">
						<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
							<line x1="12" y1="5" x2="12" y2="19" />
							<line x1="5" y1="12" x2="19" y2="12" />
						</svg>
					</span>
					<span>{labels.createButtonLabel}</span>
				</button>
			) : (
				<div className="add-to-album-list__create-form">
					<input
						type="text"
						className="add-to-album-list__input"
						placeholder={labels.inputPlaceholder}
						value={newName}
						onChange={(e) => setNewName(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === 'Enter') handleCreateSubmit()
							if (e.key === 'Escape') {
								setCreateOpen(false)
								setNewName('')
							}
						}}
						autoFocus
						maxLength={200}
					/>
					<div className="add-to-album-list__create-actions">
						<button
							type="button"
							className="add-to-album-list__btn add-to-album-list__btn--primary"
							onClick={handleCreateSubmit}
							disabled={!newName.trim() || creating}
						>
							{creating ? 'Создаём…' : 'Создать'}
						</button>
						{!isCreateOnly && (
							<button
								type="button"
								className="add-to-album-list__btn"
								onClick={() => {
									setCreateOpen(false)
									setNewName('')
								}}
							>
								Отмена
							</button>
						)}
					</div>
				</div>
			)}

			{/* Список папок */}
			{!isCreateOnly && (
				<div className="add-to-album-list__items">
					{loading && <div className="add-to-album-list__status">Загружаем…</div>}

					{!loading && error && (
						<div className="add-to-album-list__status add-to-album-list__status--error">
							{error}
						</div>
					)}

					{!loading && !error && folders.length === 0 && !createOpen && (
						<div className="add-to-album-list__status">{labels.emptyText}</div>
					)}

					{!loading && !error && folders.map((folder) => {
						const state = getState(folder.id)
						const busy = busyFolders.has(folder.id)
						return (
							<button
								key={folder.id}
								type="button"
								className={
									'add-to-album-list__row' +
									(state === 'checked' ? ' add-to-album-list__row--checked' : '') +
									(busy ? ' add-to-album-list__row--busy' : '')
								}
								onClick={() => handleToggle(folder.id)}
								disabled={busy}
							>
								<span
									className={
										'add-to-album-list__checkbox' +
										` add-to-album-list__checkbox--${state}`
									}
									aria-hidden="true"
								>
									{state === 'checked' && (
										<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
											<polyline points="20 6 9 17 4 12" />
										</svg>
									)}
									{state === 'indeterminate' && (
										<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
											<line x1="5" y1="12" x2="19" y2="12" />
										</svg>
									)}
								</span>

								<span className="add-to-album-list__cover">
									{folder.cover_url ? (
										<img src={folder.cover_url} alt="" loading="lazy" />
									) : (
										<span className="add-to-album-list__cover-placeholder" aria-hidden="true">
											<Icon id="gen-audio" size={14} />
										</span>
									)}
								</span>

								<span className="add-to-album-list__name" title={folder.name}>
									{folder.name}
								</span>

								<span className="add-to-album-list__count">
									{api.getFolderItemCount(folder)}
								</span>
							</button>
						)
					})}
				</div>
			)}
		</div>
	)
}

// ── Sheet (standalone) ────────────────────────────────────────────

interface SheetProps<F extends FolderLike> {
	open: boolean
	onClose: () => void
	itemIds: string[]
	api: FolderPickerAPI<F>
	labels: FolderPickerLabels
	onChanged: () => void
	/** DOM-элемент кнопки. На десктопе — рендерим AnchoredPopover, иначе Sheet. */
	anchorEl?: HTMLElement | null
}

const POPOVER_WIDTH = 340

export function FolderPickerSheet<F extends FolderLike>({
	open,
	onClose,
	itemIds,
	api,
	labels,
	onChanged,
	anchorEl,
}: SheetProps<F>) {
	const isDesktop = useIsDesktopPopover()
	const usePopover = isDesktop && !!anchorEl
	const isCreateOnly = itemIds.length === 0

	const title = isCreateOnly
		? labels.createTitle
		: itemIds.length === 1
			? labels.addSingleTitle
			: labels.addMultiTitle(itemIds.length)

	const body = (
		<div className={'add-to-album-sheet' + (usePopover ? ' add-to-album-sheet--popover' : '')}>
			{usePopover && <div className="add-to-album-sheet__popover-title">{title}</div>}
			<FolderPickerList
				active={open}
				itemIds={itemIds}
				api={api}
				labels={labels}
				onChanged={onChanged}
				onAfterCreate={onClose}
				compact={usePopover}
			/>
		</div>
	)

	if (usePopover && anchorEl) {
		return (
			<AnchoredPopover
				open={open}
				onClose={onClose}
				anchorEl={anchorEl}
				width={POPOVER_WIDTH}
				className="add-to-album-popover"
			>
				{body}
			</AnchoredPopover>
		)
	}

	return (
		<Sheet open={open} onClose={onClose} variant="bottom" title={title} showHandle>
			{body}
		</Sheet>
	)
}
